import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { Money } from '@principle-theorem/accounting';
import { NgPatientModule } from '@principle-theorem/ng-patient';
import {
  DisplayColourBarComponent,
  CurrentBrandScope,
  GlobalStoreService,
  NgPrincipleSharedModule,
} from '@principle-theorem/ng-principle-shared';
import { NgSharedModule } from '@principle-theorem/ng-shared';
import { OrganisationCache } from '@principle-theorem/principle-core';
import {
  AppointmentSummary,
  DepositStatus,
  EVENT_TYPES_ICONS,
  EventType,
  PatientStatus,
  UNCATEGORISED_CATEGORY,
  isUnpaidDepositStatus,
  type IDepositMetadata,
  type IPatient,
  type ITreatmentCategory,
} from '@principle-theorem/principle-core/interfaces';
import { TIME_FORMAT, type WithRef } from '@principle-theorem/shared';
import { ReplaySubject, combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { GenericTimelineTooltipComponent } from '../generic-timeline-tooltip/generic-timeline-tooltip.component';

export interface IOutstandingDepositDisplay {
  status: DepositStatus;
  /**
   * Preformatted with the bare local symbol. Staff read this inside a practice
   * whose currency is never in doubt, so the country-qualified form would only
   * add noise to a line that is already dense.
   */
  amount: string;
}

@Component({
  selector: 'pr-timeline-appointment-tooltip',
  templateUrl: './timeline-appointment-tooltip.component.html',
  styleUrls: ['./timeline-appointment-tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DisplayColourBarComponent,
    GenericTimelineTooltipComponent,
    NgSharedModule,
    NgPrincipleSharedModule,
    NgPatientModule,
    CommonModule,
  ],
})
export class TimelineAppointmentTooltipComponent {
  readonly depositsEnabled$ = inject(CurrentBrandScope).depositsEnabled$;
  readonly timeFormat = TIME_FORMAT;
  readonly patientStatus = PatientStatus;
  appointment$ = new ReplaySubject<AppointmentSummary>(1);
  eventIcon$: Observable<string>;
  patient$: Observable<WithRef<IPatient>>;
  category$: Observable<ITreatmentCategory>;
  status$: Observable<string>;
  deposit$: Observable<IDepositMetadata | undefined>;
  outstandingDepositDisplay$: Observable<
    IOutstandingDepositDisplay | undefined
  >;

  @Input()
  set appointment(appointment: AppointmentSummary) {
    if (appointment) {
      this.appointment$.next(appointment);
    }
  }

  constructor(private _global: GlobalStoreService) {
    this.eventIcon$ = of(EVENT_TYPES_ICONS[EventType.Appointment]);
    this.patient$ = this.appointment$.pipe(
      switchMap((eventable) =>
        OrganisationCache.patients.getDoc(eventable.metadata.patientRef)
      )
    );
    this.status$ = this.appointment$.pipe(
      map((appointment) =>
        appointment.metadata.status.replace(/([A-Z])/g, ' $1')
      )
    );
    this.deposit$ = this.appointment$.pipe(
      map((appointment) => appointment.metadata.deposit)
    );

    this.outstandingDepositDisplay$ = combineLatest([
      this.deposit$,
      this.depositsEnabled$,
    ]).pipe(
      map(([deposit, depositsEnabled]) =>
        depositsEnabled && isUnpaidDepositStatus(deposit?.status)
          ? deposit
          : undefined
      ),
      map((deposit) =>
        deposit
          ? {
              status: deposit.status,
              amount: Money.formatPlainCents(
                deposit.amountCents,
                deposit.currency
              ),
            }
          : undefined
      )
    );
    this.category$ = this.appointment$.pipe(
      switchMap((appointment) => {
        const displayRef = appointment.metadata.categoryRef;

        if (!displayRef) {
          return of(UNCATEGORISED_CATEGORY);
        }

        return this._global
          .getTreatmentCategory$(displayRef)
          .pipe(map((category) => category ?? UNCATEGORISED_CATEGORY));
      })
    );
  }
}
